// AI Models Service API Client
// Communicates directly with the AI Models Service at port 8100

function getAiModelsBase(): string {
  if (typeof window === 'undefined') {
    return process.env.NEXT_PUBLIC_AI_MODELS_URL || 'http://localhost:8100';
  }

  const hostname = window.location.hostname;

  // Production - use same host with different port or subdomain
  if (hostname === 'comfy-ui.nichetide.com') {
    return 'https://ai-models.nichetide.com';
  }

  return process.env.NEXT_PUBLIC_AI_MODELS_URL || 'http://localhost:8100';
}

const AI_MODELS_BASE = getAiModelsBase();

// Types
export interface HealthResponse {
  status: string;
  version: string;
  device: string;
  gpu_available: boolean;
  gpu_name?: string;
  gpu_memory_total?: number;
  gpu_memory_free?: number;
  loaded_models: string[];
  queue?: {
    pending: number;
    active: boolean;
    completed: number;
    failed: number;
    avg_time_ms: number;
  };
}

export interface YoloDetection {
  class_id: number;
  class_name: string;
  confidence: number;
  bbox: { x1: number; y1: number; x2: number; y2: number };
}

export interface YoloClassification {
  class_id: number;
  class_name: string;
  confidence: number;
}

export interface YoloResponse {
  detections?: YoloDetection[];
  classifications?: YoloClassification[];
  image_size: [number, number];
  inference_time_ms: number;
  annotated_image?: string;
}

export interface RmbgResponse {
  image: string;
  inference_time_ms: number;
}

export interface UpscaleResponse {
  image: string;
  original_size: [number, number];
  upscaled_size: [number, number];
  inference_time_ms: number;
}

export interface Sam2Mask {
  mask: string;
  area: number;
  bbox: [number, number, number, number];
  score?: number;
}

export interface Sam2Response {
  masks: Sam2Mask[];
  composite_image?: string;
  inference_time_ms: number;
}

export interface ImageGenResponse {
  image: string;
  seed: number;
  inference_time_ms: number;
}

export interface ImageGenImg2ImgResponse {
  image: string;
  prompt: string;
  width: number;
  height: number;
  num_inference_steps: number;
  guidance_scale: number;
  seed: number | null;
  model_used: string;
  inference_time_ms: number;
}

export interface DepthResponse {
  depth_map: string;
  inference_time_ms: number;
}

export interface CaptionResponse {
  caption?: string;
  tags?: string[];
  answer?: string;
  inference_time_ms: number;
}

export interface VectorizeResponse {
  svg: string;
  svg_size_bytes: number;
  image_size: [number, number];
  inference_time_ms: number;
  colormode: string;
  hierarchical: string;
}

export interface VectorizePreviewResponse {
  svg: string;
  preview_image: string;
  svg_size_bytes: number;
  image_size: [number, number];
  inference_time_ms: number;
}

// Florence types
export interface FlorenceCaptionResponse {
  caption: string;
  detail_level: string;
  image_size: [number, number];
  inference_time_ms: number;
  model: string;
}

export interface FlorenceOCRResponse {
  text?: string;
  text_regions?: number[][];
  labels?: string[];
  image_size: [number, number];
  inference_time_ms: number;
  model: string;
}

export interface FlorenceDetectResponse {
  bboxes: number[][];
  labels: string[];
  image_size: [number, number];
  inference_time_ms: number;
  model: string;
}

export interface FlorenceOpenVocabResponse {
  bboxes: number[][];
  labels: string[];
  query: string;
  image_size: [number, number];
  inference_time_ms: number;
  model: string;
}

export interface FlorenceGroundResponse {
  bboxes: number[][];
  labels: string[];
  caption: string;
  image_size: [number, number];
  inference_time_ms: number;
  model: string;
}

export interface FlorenceDenseRegionResponse {
  bboxes: number[][];
  labels: string[];
  image_size: [number, number];
  inference_time_ms: number;
  model: string;
}

export interface FlorenceGenericTaskResponse {
  task: string;
  result: unknown;
  image_size: [number, number];
  inference_time_ms: number;
  model: string;
}

// GFPGAN types
export interface GFPGANRestoreResponse {
  image: string;
  faces_detected: number;
  faces_restored: number;
  original_size: [number, number];
  restored_size: [number, number];
  upscale: number;
  inference_time_ms: number;
  model: string;
}

// ControlNet types
export interface ControlNetGenerateResponse {
  image: string;
  control_image: string;
  control_type: string;
  prompt: string;
  width: number;
  height: number;
  num_inference_steps: number;
  guidance_scale: number;
  controlnet_conditioning_scale: number;
  seed: number | null;
  inference_time_ms: number;
}

// IP-Adapter types
export interface IPAdapterGenerateResponse {
  image: string;
  prompt: string;
  adapter_type: string;
  width: number;
  height: number;
  num_inference_steps: number;
  guidance_scale: number;
  ip_adapter_scale: number;
  seed: number | null;
  inference_time_ms: number;
}

// SVD types
export interface SVDGenerateResponse {
  video: string;
  num_frames: number;
  fps: number;
  width: number;
  height: number;
  motion_bucket_id: number;
  seed: number;
  inference_time_ms: number;
  model_variant: string;
}

export interface SVDModelInfo {
  variant: string;
  description: string;
  default_frames: number;
}

// Multiple Angles types
export interface MultipleAnglesResponse {
  image: string;
  prompt: string;
  azimuth: number;
  azimuth_descriptor: string;
  elevation: number;
  elevation_descriptor: string;
  distance: number;
  distance_descriptor: string;
  lora_scale: number;
  guidance_scale: number;
  num_inference_steps: number;
  seed: number | null;
  inference_time_ms: number;
}

export interface MultipleAnglesBatchResponse {
  images: MultipleAnglesResponse[];
  total_inference_time_ms: number;
}

export interface PoseConfig {
  azimuth: 0 | 45 | 90 | 135 | 180 | 225 | 270 | 315;
  elevation: -30 | 0 | 30 | 60;
  distance: 0.6 | 1.0 | 1.8;
}

export interface AngleOption {
  angle: number;
  descriptor: string;
}

export interface DistanceOption {
  scale: number;
  descriptor: string;
}

export interface CameraOptions {
  azimuths: AngleOption[];
  elevations: AngleOption[];
  distances: DistanceOption[];
}

// LTX-Video types
export interface LTXVideoResponse {
  video: string;
  width: number;
  height: number;
  num_frames: number;
  fps: number;
  duration_seconds: number;
  prompt: string;
  upscaled: boolean;
  seed: number | null;
  inference_time_ms: number;
}

export interface LTXVideoResolutionOption {
  width: number;
  height: number;
  label: string;
}

export interface LTXVideoFrameCountOption {
  frames: number;
  duration: string;
}

export interface LTXVideoOptionsResponse {
  resolutions: LTXVideoResolutionOption[];
  frame_counts: LTXVideoFrameCountOption[];
  default_negative_prompt: string;
}

// LTX-2.3 types
export interface LTX2Response {
  video: string;
  thumbnail: string;
  width: number;
  height: number;
  num_frames: number;
  fps: number;
  duration_seconds: number;
  prompt: string;
  has_audio: boolean;
  mode: string;
  seed: number | null;
  inference_time_ms: number;
}

export interface LTX2OptionsResponse {
  resolutions: LTXVideoResolutionOption[];
  frame_counts: LTXVideoFrameCountOption[];
  default_negative_prompt: string;
  modes: string[];
}

// Sulphur types
export interface SulphurResponse {
  video: string;
  width: number;
  height: number;
  num_frames: number;
  fps: number;
  duration_seconds: number;
  prompt: string;
  seed: number | null;
  inference_time_ms: number;
}

export interface SulphurResolutionOption {
  width: number;
  height: number;
  label: string;
}

export interface SulphurFrameCountOption {
  frames: number;
  duration: string;
}

export interface SulphurOptionsResponse {
  resolutions: SulphurResolutionOption[];
  frame_counts: SulphurFrameCountOption[];
  default_negative_prompt: string;
}

// HunyuanVideo types
export interface HunyuanVideoResponse {
  video: string;
  width: number;
  height: number;
  num_frames: number;
  fps: number;
  duration_seconds: number;
  inference_time_ms: number;
}

export interface HunyuanVideoResolutionOption {
  quality: string;
  aspect: string;
  width: number;
  height: number;
  label: string;
}

export interface HunyuanVideoFrameCountOption {
  frames: number;
  duration: string;
}

export interface HunyuanVideoOptionsResponse {
  resolutions: HunyuanVideoResolutionOption[];
  frame_counts: HunyuanVideoFrameCountOption[];
}

class AiModelsApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = AI_MODELS_BASE) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const aiKey = process.env.NEXT_PUBLIC_AI_MODELS_KEY;
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(aiKey ? { Authorization: `Bearer ${aiKey}` } : {}),
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      // Handle FastAPI validation errors (422) which have detail as array
      let message = `API error: ${response.status}`;
      if (typeof error.detail === 'string') {
        message = error.detail;
      } else if (Array.isArray(error.detail)) {
        // Validation errors from FastAPI
        message = error.detail.map((e: { msg?: string; loc?: string[] }) =>
          e.msg || JSON.stringify(e)
        ).join(', ');
      } else if (error.detail) {
        message = JSON.stringify(error.detail);
      }
      throw new Error(message);
    }

    return response.json();
  }

  // Health
  async getHealth(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health');
  }

  // YOLO
  async yoloDetect(image: string, params: {
    confidence?: number;
    iou?: number;
    classes?: number[];
    model_variant?: string;
    return_annotated?: boolean;
  } = {}): Promise<YoloResponse> {
    return this.request<YoloResponse>('/yolo/detect', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  async yoloSegment(image: string, params: {
    confidence?: number;
    iou?: number;
    model_variant?: string;
    return_annotated?: boolean;
  } = {}): Promise<YoloResponse> {
    return this.request<YoloResponse>('/yolo/segment', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  async yoloPose(image: string, params: {
    confidence?: number;
    model_variant?: string;
    return_annotated?: boolean;
  } = {}): Promise<YoloResponse> {
    return this.request<YoloResponse>('/yolo/pose', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  async yoloClassify(image: string, params: {
    top_k?: number;
    model_variant?: string;
  } = {}): Promise<YoloResponse> {
    return this.request<YoloResponse>('/yolo/classify', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // RMBG
  async rmbgRemove(
    image: string,
    opts?: { model?: string; alpha_matting?: boolean },
  ): Promise<RmbgResponse> {
    return this.request<RmbgResponse>('/rmbg/remove', {
      method: 'POST',
      body: JSON.stringify({ image, ...opts }),
    });
  }

  async rmbgMask(image: string): Promise<RmbgResponse> {
    return this.request<RmbgResponse>('/rmbg/mask', {
      method: 'POST',
      body: JSON.stringify({ image }),
    });
  }

  // Upscale
  async upscaleImage(image: string, scale: number = 2): Promise<UpscaleResponse> {
    return this.request<UpscaleResponse>('/upscale/image', {
      method: 'POST',
      body: JSON.stringify({ image, scale }),
    });
  }

  // SAM2
  async sam2SegmentPoint(image: string, points: { x: number; y: number; label: number }[]): Promise<Sam2Response> {
    // Backend expects points as [[x, y], ...] and labels as [label, ...]
    const pointCoords = points.map(p => [Math.round(p.x), Math.round(p.y)]);
    const labels = points.map(p => p.label);
    return this.request<Sam2Response>('/sam2/segment/point', {
      method: 'POST',
      body: JSON.stringify({ image, points: pointCoords, labels }),
    });
  }

  async sam2SegmentBox(image: string, box: { x1: number; y1: number; x2: number; y2: number }): Promise<Sam2Response> {
    // Endpoint expects boxes: list[list[int]] ([[x1, y1, x2, y2]]).
    return this.request<Sam2Response>('/sam2/segment/box', {
      method: 'POST',
      body: JSON.stringify({
        image,
        boxes: [[box.x1, box.y1, box.x2, box.y2].map((v) => Math.round(v))],
      }),
    });
  }

  async sam2SegmentAuto(image: string, params: {
    points_per_side?: number;
    pred_iou_thresh?: number;
    stability_score_thresh?: number;
  } = {}): Promise<Sam2Response> {
    return this.request<Sam2Response>('/sam2/segment/auto', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // ImageGen
  async imagegenGenerate(params: {
    prompt: string;
    width?: number;
    height?: number;
    num_inference_steps?: number;
    seed?: number | null;
    model?: string;
  }): Promise<ImageGenResponse> {
    return this.request<ImageGenResponse>('/imagegen/generate', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async imagegenImg2Img(image: string, params: {
    prompt: string;
    width?: number;
    height?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    seed?: number | null;
    model?: string;
    output_format?: string;
    jpeg_quality?: number;
  }): Promise<ImageGenImg2ImgResponse> {
    return this.request<ImageGenImg2ImgResponse>('/imagegen/generate/img2img', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // Depth
  async depthEstimate(image: string): Promise<DepthResponse> {
    return this.request<DepthResponse>('/depth/estimate', {
      method: 'POST',
      body: JSON.stringify({ image }),
    });
  }

  // Caption
  async captionGenerate(image: string): Promise<CaptionResponse> {
    return this.request<CaptionResponse>('/caption/generate', {
      method: 'POST',
      body: JSON.stringify({ image }),
    });
  }

  async captionTags(image: string): Promise<CaptionResponse> {
    return this.request<CaptionResponse>('/caption/tags', {
      method: 'POST',
      body: JSON.stringify({ image }),
    });
  }

  async captionQuestion(image: string, question: string): Promise<CaptionResponse> {
    return this.request<CaptionResponse>('/caption/question', {
      method: 'POST',
      body: JSON.stringify({ image, question }),
    });
  }

  // Vectorize
  async vectorizeConvert(image: string, params: {
    colormode?: 'color' | 'binary';
    hierarchical?: 'stacked' | 'cutout';
    filter_speckle?: number;
    color_precision?: number;
    layer_difference?: number;
    corner_threshold?: number;
    length_threshold?: number;
    splice_threshold?: number;
    path_precision?: number;
  } = {}): Promise<VectorizeResponse> {
    return this.request<VectorizeResponse>('/vectorize/convert', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  async vectorizePreview(image: string, params: {
    colormode?: 'color' | 'binary';
    hierarchical?: 'stacked' | 'cutout';
    filter_speckle?: number;
    color_precision?: number;
    layer_difference?: number;
    corner_threshold?: number;
    length_threshold?: number;
    splice_threshold?: number;
    path_precision?: number;
    preview_width?: number;
    preview_height?: number;
  } = {}): Promise<VectorizePreviewResponse> {
    return this.request<VectorizePreviewResponse>('/vectorize/preview', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // Florence
  async florenceCaption(image: string, params: {
    model?: string;
    detail_level?: 'brief' | 'normal' | 'detailed';
  } = {}): Promise<FlorenceCaptionResponse> {
    return this.request<FlorenceCaptionResponse>('/florence/caption', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  async florenceOCR(image: string, params: {
    model?: string;
    with_regions?: boolean;
  } = {}): Promise<FlorenceOCRResponse> {
    return this.request<FlorenceOCRResponse>('/florence/ocr', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  async florenceDetect(image: string, params: {
    model?: string;
  } = {}): Promise<FlorenceDetectResponse> {
    return this.request<FlorenceDetectResponse>('/florence/detect', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  async florenceDetectOpenVocab(image: string, classes: string, params: {
    model?: string;
  } = {}): Promise<FlorenceOpenVocabResponse> {
    return this.request<FlorenceOpenVocabResponse>('/florence/detect/open-vocab', {
      method: 'POST',
      body: JSON.stringify({ image, classes, ...params }),
    });
  }

  async florenceGround(image: string, caption: string, params: {
    model?: string;
  } = {}): Promise<FlorenceGroundResponse> {
    return this.request<FlorenceGroundResponse>('/florence/ground', {
      method: 'POST',
      body: JSON.stringify({ image, caption, ...params }),
    });
  }

  async florenceDenseCaption(image: string, params: {
    model?: string;
  } = {}): Promise<FlorenceDenseRegionResponse> {
    return this.request<FlorenceDenseRegionResponse>('/florence/dense-caption', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  async florenceTask(image: string, task: string, params: {
    model?: string;
    text_input?: string;
    max_new_tokens?: number;
  } = {}): Promise<FlorenceGenericTaskResponse> {
    return this.request<FlorenceGenericTaskResponse>('/florence/task', {
      method: 'POST',
      body: JSON.stringify({ image, task, ...params }),
    });
  }

  // GFPGAN
  async gfpganRestore(image: string, params: {
    upscale?: number;
    only_center_face?: boolean;
    aligned?: boolean;
    paste_back?: boolean;
    model?: string;
  } = {}): Promise<GFPGANRestoreResponse> {
    return this.request<GFPGANRestoreResponse>('/gfpgan/restore', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // ControlNet
  async controlnetGenerate(image: string, prompt: string, params: {
    control_type?: string;
    negative_prompt?: string;
    width?: number;
    height?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    controlnet_conditioning_scale?: number;
    seed?: number | null;
    preprocess?: boolean;
    canny_low_threshold?: number;
    canny_high_threshold?: number;
  } = {}): Promise<ControlNetGenerateResponse> {
    return this.request<ControlNetGenerateResponse>('/controlnet/generate', {
      method: 'POST',
      body: JSON.stringify({ image, prompt, ...params }),
    });
  }

  // IP-Adapter
  async ipadapterGenerate(referenceImage: string, params: {
    prompt?: string;
    negative_prompt?: string;
    adapter_type?: string;
    width?: number;
    height?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    ip_adapter_scale?: number | null;
    seed?: number | null;
  } = {}): Promise<IPAdapterGenerateResponse> {
    return this.request<IPAdapterGenerateResponse>('/ipadapter/generate', {
      method: 'POST',
      body: JSON.stringify({ reference_image: referenceImage, ...params }),
    });
  }

  // SVD (Stable Video Diffusion)
  async svdModels(): Promise<SVDModelInfo[]> {
    return this.request<SVDModelInfo[]>('/svd/models');
  }

  async svdGenerate(image: string, params: {
    model_variant?: string;
    num_frames?: number;
    fps?: number;
    motion_bucket_id?: number;
    noise_aug_strength?: number;
    decode_chunk_size?: number;
    num_inference_steps?: number;
    min_guidance_scale?: number;
    max_guidance_scale?: number;
    seed?: number | null;
  } = {}): Promise<SVDGenerateResponse> {
    return this.request<SVDGenerateResponse>('/svd/generate', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // Multiple Angles
  async multipleAnglesOptions(): Promise<CameraOptions> {
    return this.request<CameraOptions>('/multiple-angles/options');
  }

  async multipleAnglesGenerate(image: string, params: {
    azimuth?: number;
    elevation?: number;
    distance?: number;
    lora_scale?: number;
    guidance_scale?: number;
    num_inference_steps?: number;
    seed?: number | null;
    output_format?: 'PNG' | 'JPEG';
    jpeg_quality?: number;
  } = {}): Promise<MultipleAnglesResponse> {
    return this.request<MultipleAnglesResponse>('/multiple-angles/generate', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  async multipleAnglesBatch(image: string, poses: PoseConfig[], params: {
    lora_scale?: number;
    guidance_scale?: number;
    num_inference_steps?: number;
    seed?: number | null;
  } = {}): Promise<MultipleAnglesBatchResponse> {
    return this.request<MultipleAnglesBatchResponse>('/multiple-angles/generate/batch', {
      method: 'POST',
      body: JSON.stringify({ image, poses, ...params }),
    });
  }

  // LTX-Video
  async ltxVideoOptions(): Promise<LTXVideoOptionsResponse> {
    return this.request<LTXVideoOptionsResponse>('/ltx-video/options');
  }

  async ltxVideoT2V(params: {
    prompt: string;
    negative_prompt?: string;
    width?: number;
    height?: number;
    num_frames?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    upscale?: boolean;
    seed?: number | null;
  }): Promise<LTXVideoResponse> {
    return this.request<LTXVideoResponse>('/ltx-video/t2v', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async ltxVideoI2V(image: string, params: {
    prompt: string;
    negative_prompt?: string;
    width?: number;
    height?: number;
    num_frames?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    upscale?: boolean;
    seed?: number | null;
  }): Promise<LTXVideoResponse> {
    return this.request<LTXVideoResponse>('/ltx-video/i2v', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // Sulphur
  async sulphurOptions(): Promise<SulphurOptionsResponse> {
    return this.request<SulphurOptionsResponse>('/sulphur/options');
  }

  async sulphurT2V(params: {
    prompt: string;
    negative_prompt?: string;
    width?: number;
    height?: number;
    num_frames?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    seed?: number | null;
  }): Promise<SulphurResponse> {
    return this.request<SulphurResponse>('/sulphur/t2v', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async sulphurI2V(image: string, params: {
    prompt: string;
    negative_prompt?: string;
    width?: number;
    height?: number;
    num_frames?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    seed?: number | null;
  }): Promise<SulphurResponse> {
    return this.request<SulphurResponse>('/sulphur/i2v', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // HunyuanVideo
  async hunyuanVideoOptions(): Promise<HunyuanVideoOptionsResponse> {
    return this.request<HunyuanVideoOptionsResponse>('/hunyuan-video/options');
  }

  async hunyuanVideoT2V(params: {
    prompt: string;
    negative_prompt?: string;
    width?: number;
    height?: number;
    num_frames?: number;
    num_inference_steps?: number;
    seed?: number | null;
  }): Promise<HunyuanVideoResponse> {
    return this.request<HunyuanVideoResponse>('/hunyuan-video/t2v', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async hunyuanVideoI2V(image: string, params: {
    prompt: string;
    negative_prompt?: string;
    num_frames?: number;
    num_inference_steps?: number;
    seed?: number | null;
  }): Promise<HunyuanVideoResponse> {
    return this.request<HunyuanVideoResponse>('/hunyuan-video/i2v', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // LTX-2.3
  async ltx2Options(): Promise<LTX2OptionsResponse> {
    return this.request<LTX2OptionsResponse>('/ltx-2/options');
  }

  async ltx2T2V(params: {
    prompt: string;
    negative_prompt?: string;
    width?: number;
    height?: number;
    num_frames?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    stg_scale?: number;
    audio_enabled?: boolean;
    seed?: number | null;
  }): Promise<LTX2Response> {
    return this.request<LTX2Response>('/ltx-2/t2v', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async ltx2I2V(image: string, params: {
    prompt: string;
    negative_prompt?: string;
    width?: number;
    height?: number;
    num_frames?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    stg_scale?: number;
    image_strength?: number;
    audio_enabled?: boolean;
    seed?: number | null;
  }): Promise<LTX2Response> {
    return this.request<LTX2Response>('/ltx-2/i2v', {
      method: 'POST',
      body: JSON.stringify({ image, ...params }),
    });
  }

  // Helper to convert file to base64
  static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}

export const aiModelsApi = new AiModelsApiClient();
export default aiModelsApi;
